import { Link as AmplifyLink, Button, Flex, Image, WithAuthenticatorProps } from '@aws-amplify/ui-react';
import './SideNav.scss';

import { useContext, useEffect, useState } from 'react';
import { AiOutlineArrowRight, AiOutlineBank, AiOutlineEnvironment, AiOutlineNotification, AiOutlineTeam } from 'react-icons/ai';
import { BsLayoutSidebar } from 'react-icons/bs';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { FiLogOut } from 'react-icons/fi';
import { HiSquares2X2 } from 'react-icons/hi2';
import { Link, NavLink, useLocation } from 'react-router-dom';
import General from 'src/utils/GeneralUtils';
import { DEFAULT_NAVIGATOR_QUERY, serializeNavigatorQuery } from '../components/navigator/navigator-query';
import { GlobalContext, GlobalContextProps } from '../global-context';
import { LocalStorageKeys, LocalStorageService } from '../utils/LocalStorageService';

async function signOut(withAuthenticatorProps: WithAuthenticatorProps) {
  if (withAuthenticatorProps.signOut) {
    console.log(`Starting to sign the use out`)
    withAuthenticatorProps.signOut()
    console.log(`User signed out`)
  }
}

function formatUserName(userAttributes?: { given_name?: string; family_name?: string }): string {
  if (!userAttributes) return '';

  const firstName = userAttributes.given_name || '';
  const lastName = userAttributes.family_name || '';

  if (!firstName && !lastName) return '';
  if (!lastName) return firstName;

  const lastInitial = lastName.charAt(0).toUpperCase() + '.';
  return `${firstName} ${lastInitial}`;
}

/** The Navigator's map on its default query, where the left menu takes you. */
const NAVIGATOR_HREF = `/navigator/search/map?q=${serializeNavigatorQuery(DEFAULT_NAVIGATOR_QUERY)}`;

export function getNavLinkClassName(params: any, level: number = 1, className: string = ''): string {
  const { isActive, isPending } = params;
  return isPending
    ? [className, 'link', 'pending', `link-level-${level}`].filter(it => !!it).join(' ')
    : isActive
      ? [className, 'link', 'active', `link-level-${level}`].filter(it => !!it).join(' ')
      : [className, 'link', `link-level-${level}`].filter(it => !!it).join(' ')
}

export function SideNav() {
  const globalContext = useContext(GlobalContext);
  const location = useLocation();
  const [isSidebarVisible, setIsSidebarVisible] = useState(() => {
    return LocalStorageService.get<boolean>(LocalStorageKeys.IS_SIDEBAR_VISIBLE);
  });
  const [expandedSettings, setExpandedSettings] = useState(() => {
    // Auto-expand if we're on any settings page
    return location.pathname.startsWith('/settings');
  });
  const [expandedCrmEnrichment, setExpandedCrmEnrichment] = useState(() => {
    // Auto-expand if we're on any CRM enrichment page
    return location.pathname.startsWith('/tools/hubspot-companies');
  });

  // Toggle body class when sidebar visibility changes
  useEffect(() => {
    if (isSidebarVisible) {
      document.body.classList.remove('sidebar-hidden');
    } else {
      document.body.classList.add('sidebar-hidden');
    }
    LocalStorageService.set(LocalStorageKeys.IS_SIDEBAR_VISIBLE, isSidebarVisible);
  }, [isSidebarVisible]);

  return <GlobalContext.Consumer>
    {
      (globalContextProps: GlobalContextProps) => <>
        <div className={`side-nav ${!isSidebarVisible ? 'side-nav--hidden' : ''}`}>
          <div className='top-nav-items'>
            <div className="inner variant-2" style={{ padding: '17px 5px 17px 15px' }}>
              <Flex alignItems='center' justifyContent='space-between'>
                <Link to='/' className='logo'>
                  {/* TODO: Once we move to dark mode everywhere, always use /logo-edulead-dark.svg */}
                  <Image
                    src={globalContextProps.theme === 'dark' ? '/logo-edulead-dark.svg' : `/${globalContext.environment.logo}`}
                    width={globalContext.environment.logoWidth}
                    alt={globalContext.environment.name}
                  />
                </Link>
                <Button
                  size='small'
                  onClick={() => setIsSidebarVisible(false)}
                  className='nohover close-sidebar-btn'
                >
                  <BsLayoutSidebar size={14} />
                </Button>
              </Flex>
            </div>
            <ul>
              {/* <NavLink to='/agent' style={{ position: 'relative' }} className={(params) => getNavLinkClassName(params, 1, 'magic')}>GPT <span className='beta'>new</span></NavLink> */}
              <NavLink
                to='/feed'
                className={getNavLinkClassName}
              >
                <Flex alignItems='center' gap={9}>
                  <AiOutlineNotification /> Feed
                </Flex>
              </NavLink>
              <NavLink
                // The Navigator opens on its default query (the map counts at once); active
                // on any of its views, which the path alone would not say.
                to={NAVIGATOR_HREF}
                className={({ isActive, isPending }) => getNavLinkClassName({ isActive: isActive || location.pathname.startsWith('/navigator'), isPending })}
                style={{ position: 'relative' }}
              >
                <Flex alignItems='center' gap={9}>
                  <AiOutlineEnvironment /> Navigator
                </Flex>
                <span className='new'>new</span>
              </NavLink>
              <NavLink
                to='/buying-groups'
                className={getNavLinkClassName}
              >
                <Flex alignItems='center' gap={9}>
                  <HiSquares2X2 /> Buying Groups
                </Flex>
              </NavLink>
              <span className='side-nav-title' style={{ marginTop: 10 }}>Directory</span>
              <NavLink
                to="/directory/people"
                className={getNavLinkClassName}
              >
                <Flex alignItems='center' gap={9}>
                  <AiOutlineTeam /> People
                </Flex>
              </NavLink>
              <NavLink
                to="/directory/institutions"
                className={getNavLinkClassName}
              >
                <Flex alignItems='center' gap={9}>
                  <AiOutlineBank /> Institutions
                </Flex>
              </NavLink>
              {/* <NavLink
                to={`/directory/${ globalContext.environment.entities.rfps.urlSegment }${ typeof globalContextProps.authInfo?.team?.defaultRFPsDirectoryQueryString === 'string' ? globalContextProps.authInfo?.team?.defaultRFPsDirectoryQueryString : globalContext.environment.entities.rfps.defaultQueryString }`}
                className={getNavLinkClassName}
                style={{ position: 'relative' }}
              >
                <Flex alignItems='center' gap={9}>
                  <AiOutlineRocket /> { globalContext.environment.entities.rfps.namePlural } <span className='beta'>beta</span>
                </Flex>
              </NavLink> */}
              {/* <NavLink
                to={`/directory/${ globalContext.environment.entities.domains.urlSegment }${ typeof globalContextProps.authInfo?.team?.defaultDomainsDirectoryQueryString === 'string' ? globalContextProps.authInfo?.team?.defaultDomainsDirectoryQueryString : globalContext.environment.entities.domains.defaultQueryString }`}
                className={getNavLinkClassName}
                style={{ position: 'relative' }}
              >
                { globalContext.environment.entities.domains.namePlural }
              </NavLink> */}

              <span className='side-nav-title'>Tools</span>
              {
                globalContext.environment.flags.shouldEnableTrackingPage &&
                  <NavLink
                    to='/tracking'
                    className={getNavLinkClassName}
                    style={{ position: 'relative' }}
                  >
                    Web Tracking
                  </NavLink>
              }
              <div
                className='link'
                style={{ position: 'relative', cursor: 'pointer' }}
                onClick={() => setExpandedCrmEnrichment(!expandedCrmEnrichment)}
              >
                <span>HubSpot Enrichment</span>
                <span>
                  {expandedCrmEnrichment ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                </span>
              </div>
              {expandedCrmEnrichment && (
                <NavLink
                  to='/tools/hubspot-companies'
                  className={(params) => getNavLinkClassName(params, 2)}
                  // The badge is absolutely positioned at the row's right edge, so it goes
                  // on the child rather than on CRM Enrichment, where it would sit on top
                  // of the expand chevron.
                  style={{ marginLeft: '20px', position: 'relative' }}
                >
                  Companies <span className='beta'>beta</span>
                </NavLink>
              )}
              {/* <NavLink to='/gpt' className={getNavLinkClassName} style={{ position: 'relative' }}>LeadGPT <span className='beta'>beta</span></NavLink> */}
              {/* <FaMagic className='magic' /> */}
              {/* <NavLink
                to='/fulltext'
                className={getNavLinkClassName}
                style={{ position: 'relative' }}
              >
                Fulltext <span className='beta'>beta</span>
              </NavLink> */}
              {/* {
                globalContext.environment.flags.shouldEnableProspectorPage &&
                  <NavLink to='/prospector' style={{ position: 'relative' }} className={getNavLinkClassName}>Prospector</NavLink>
              } */}
              <NavLink to='/api' className={getNavLinkClassName} style={{ position: 'relative' }}>API</NavLink>
            </ul>
          </div>
          <div className='bottom-nav-items'>
            <div className="inner variant-1">
              <ul style={{ marginBottom: 0 }}>
                <NavLink to='/exports' className={getNavLinkClassName} style={{ position: 'relative' }}>Exports</NavLink>
                <>
                  <div
                    className='link'
                    style={{ position: 'relative', cursor: 'pointer' }}
                    onClick={() => setExpandedSettings(!expandedSettings)}
                  >
                    <span>Settings</span>
                    <span>
                      {expandedSettings ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                    </span>
                  </div>
                  {expandedSettings && (
                    <>
                      <NavLink
                        to='/settings/team' 
                        className={(params) => getNavLinkClassName(params, 2)}
                        style={{ marginLeft: '20px' }}
                      >
                        Team
                      </NavLink>
                      <NavLink 
                        to='/settings/service-limits' 
                        className={(params) => getNavLinkClassName(params, 2)}
                        style={{ marginLeft: '20px' }}
                      >
                        Service Limits
                      </NavLink>
                      <NavLink
                        to='/settings/integrations/hubspot'
                        className={(params) => getNavLinkClassName(params, 2)}
                        style={{ marginLeft: '20px' }}
                      >
                        HubSpot
                      </NavLink>
                      <NavLink
                        to='/settings/integrations/salesforce'
                        className={(params) => getNavLinkClassName(params, 2)}
                        style={{ marginLeft: '20px' }}
                      >
                        Salesforce
                      </NavLink>
                      <NavLink
                        to='/settings/billing'
                        className={(params) => getNavLinkClassName(params, 2)}
                        style={{ marginLeft: '20px' }}
                      >
                        Billing
                      </NavLink>
                    </>
                  )}
                </>
                {/* Support, below Settings, for paying teams only */}
                {General.isPaidTier(globalContextProps.authInfo?.team?.tier) && (
                  <NavLink to='/support' className={getNavLinkClassName}>
                    Support
                  </NavLink>
                )}
                <div
                  onClick={() => globalContextProps.setTheme(globalContextProps.theme === 'light' ? 'dark' : 'light')}
                  className='link'
                >
                  {globalContextProps.theme === 'light' ? 'Dark' : 'Light'} Mode
                </div>
                <Flex
                  className='sign-out-container'
                  justifyContent='space-between'
                  alignItems='center'
                  gap={10}
                >
                  <NavLink
                    to='/profile'
                    className={getNavLinkClassName}
                  >
                    {formatUserName(globalContextProps.authInfo?.userAttributes)}
                  </NavLink>
                  <span>
                    <Button
                      size='small'
                      onClick={() => signOut(globalContextProps.withAuthenticatorProps)}
                      className='nohover'
                    >
                      <FiLogOut size={13} />
                    </Button>
                  </span>
                </Flex>
              </ul>
            </div>
            <div className="inner variant-2">
              <div>© {General.getYear()} Edulead, Inc.</div>
              <div>All Rights Reserved</div>
              <Flex justifyContent='space-between'>
                <div>
                  <AmplifyLink href={globalContext.environment.urlPrivacy} target='_blank'>Privacy</AmplifyLink>
                  <AmplifyLink href={globalContext.environment.urlTerms} target='_blank' style={{ marginLeft: 5 }}>Terms</AmplifyLink>
                </div>
                <div>v1.225.0</div>
              </Flex>
            </div>
          </div>
        </div>
        {!isSidebarVisible && (
          <div
            onClick={() => setIsSidebarVisible(true)}
            className='open-sidebar-btn'
            title='Open sidebar'
          >
            <AiOutlineArrowRight size={14} />
          </div>
        )}
      </>
    }
  </GlobalContext.Consumer>
}
